import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import HubPageShell from '../components/home/HubPageShell';
import styles from './NotificationSettings.module.css';

function Switch({ checked, onChange, disabled, labelledBy }) {
  return (
    <button
      type="button"
      className={styles.switch}
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.switchThumb} aria-hidden="true" />
    </button>
  );
}

export default function NotificationSettings() {
  const [networkingNotifications, setNetworkingNotifications] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const saveGeneration = useRef(0);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await api.get('/api/user/notification-settings');
      setNetworkingNotifications(response.data.networkingNotifications !== false);
    } catch {
      setLoadError('Could not load notification settings.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInvitationToggle = async (next) => {
    const previous = networkingNotifications;
    setNetworkingNotifications(next);
    const gen = ++saveGeneration.current;
    setSaveState('saving');
    try {
      await api.post('/api/user/notification-settings', { networkingNotifications: next });
      if (saveGeneration.current === gen) setSaveState('saved');
    } catch {
      if (saveGeneration.current === gen) {
        setNetworkingNotifications(previous);
        setSaveState('error');
      }
    }
  };

  const statusLabel =
    saveState === 'saving' ? 'Saving' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Not saved' : '';

  return (
    <HubPageShell title="Notifications">
      <div className={styles.page}>
        <header className={styles.header}>
          <p className={styles.lead}>Choose which emails Velox sends you.</p>
          <div
            className={`${styles.status} ${
              saveState === 'saving'
                ? styles.statusSaving
                : saveState === 'saved'
                  ? styles.statusSaved
                  : saveState === 'error'
                    ? styles.statusError
                    : ''
            }`}
            aria-live="polite"
          >
            {statusLabel}
          </div>
        </header>

        {loadError ? (
          <div className={styles.error} role="alert">
            {loadError}
            <button type="button" className={styles.retry} onClick={fetchSettings}>
              Try again
            </button>
          </div>
        ) : null}

        {isLoading ? (
          <div className={styles.skeleton} aria-busy="true">
            <div className={styles.skelBlock} />
            <div className={styles.skelBlock} />
          </div>
        ) : (
          <>
            <section className={styles.section} aria-labelledby="connections-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>People</p>
                <h2 className={styles.sectionTitle} id="connections-title">
                  Connections
                </h2>
              </header>
              <div className={styles.row}>
                <div className={styles.copy}>
                  <p className={styles.title} id="invitation-emails-label">
                    Connection invitation emails
                  </p>
                  <p className={styles.hint}>
                    When someone invites you to connect. In-app invitations still appear in Notifications.
                  </p>
                </div>
                <Switch
                  checked={networkingNotifications}
                  onChange={handleInvitationToggle}
                  disabled={saveState === 'saving'}
                  labelledBy="invitation-emails-label"
                />
              </div>
            </section>

            <section className={styles.section} aria-labelledby="intelligence-title">
              <header className={styles.sectionHead}>
                <p className={styles.eyebrow}>Market Intelligence</p>
                <h2 className={styles.sectionTitle} id="intelligence-title">
                  Briefings
                </h2>
                <p className={styles.sectionIntro}>
                  Weekly Velox briefing and Important-move emails follow your Watchlist and topics.
                </p>
              </header>
              <Link className={styles.link} to="/insight-preferences">
                Manage in Insight preferences
              </Link>
            </section>

            <p className={styles.note}>
              Verification, password reset and order emails are always sent.
            </p>
          </>
        )}
      </div>
    </HubPageShell>
  );
}
